import { auth } from '@/lib/firebase';
import { toast } from 'sonner';
import { EDITOR_SESSIONS_CHANGED, type EditorSession } from '@/lib/editor-sessions';
export * from '@/lib/editor-sessions';

const pendingReads = new Map<string, Promise<EditorSession[]>>();
export function requestEditorSessions(
  userId: string,
  projectId: string,
  method = 'GET',
  entry?: EditorSession
): Promise<EditorSession[]> {
  const key = JSON.stringify([userId, projectId]);
  if (method === 'GET' && pendingReads.has(key)) return pendingReads.get(key)!;
  const request = (async () => {
    const user = auth.currentUser;
    if (!user || user.uid !== userId) throw new Error('Please sign in again.');
    const token = await user.getIdToken();
    const response = await fetch(
      `/api/editor-sessions?projectId=${encodeURIComponent(projectId)}`,
      {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        ...(entry ? { body: JSON.stringify(entry) } : {}),
      }
    );
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Unable to load editing sessions');
    if (method !== 'GET') pendingReads.delete(key);
    return result.sessions as EditorSession[];
  })();
  if (method === 'GET') {
    pendingReads.set(key, request);
    void request
      .finally(() => {
        if (pendingReads.get(key) === request) pendingReads.delete(key);
      })
      .catch(() => {});
  }
  return request;
}

export function rememberEditorSession(userId: string, projectId: string, entry: EditorSession) {
  void requestEditorSessions(userId, projectId, 'POST', entry)
    .then(() => {
      window.dispatchEvent(new Event(EDITOR_SESSIONS_CHANGED));
    })
    .catch(() =>
      toast.error('Editing history could not be saved. Your conversation is still saved.')
    );
}
